import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, Send } from 'lucide-react';
import { formatCATDateTime } from '../utils/timezone';
import { deptLabel, formatVitals } from './workflow/VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '—');
const URGENCY_CLS = { Routine: 'bg-gray-100 text-gray-800', Urgent: 'bg-amber-100 text-amber-900', Emergency: 'bg-red-100 text-red-800' };

// Printable external referral letter — available once the Acacia office has approved the referral.
export default function PrintReferralLetter() {
  const { visitId } = useParams();
  const [visit, setVisit] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    axios.get(`${API}/visits/${visitId}`, { withCredentials: true })
      .then((r) => { setVisit(r.data); setState('ok'); })
      .catch((e) => setState(e.response?.status === 401 ? 'login' : 'error'));
  }, [visitId]);

  if (state === 'login') { window.location.href = '/login'; return null; }
  if (state === 'loading') return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading…</div>;
  if (state === 'error' || !visit) return <div className="flex items-center justify-center min-h-screen text-gray-600">Visit not found</div>;

  const ref = visit.external_referral;
  if (!ref) return <div className="flex items-center justify-center min-h-screen text-gray-600" data-testid="referral-letter-none">No external referral was made on this visit.</div>;
  if (ref.status !== 'approved') {
    return (
      <div className="flex items-center justify-center min-h-screen p-6">
        <div className="max-w-md bg-amber-50 border border-amber-300 rounded-xl p-6 text-center text-amber-900" data-testid="referral-letter-pending">
          <p className="font-bold text-lg mb-1">{ref.status === 'rejected' ? 'Referral not approved' : 'Awaiting Acacia office approval'}</p>
          <p className="text-sm">{ref.status === 'rejected' ? `The Acacia office rejected this external referral${ref.reject_reason ? `: ${ref.reject_reason}` : ''}.` : 'The referral letter can be printed once the Acacia office approves the external referral.'}</p>
        </div>
      </div>
    );
  }

  const consults = [...(visit.consultation_history || []), ...(visit.consultation ? [visit.consultation] : [])];
  const c = consults.find((x) => x.department === ref.from_department) || consults[consults.length - 1] || {};
  const vitals = formatVitals(c.vitals) || formatVitals(visit.vitals);
  const tests = ['lab_orders', 'rad_orders', 'dental_orders'].flatMap((k) => (visit[k] || []).filter((o) => o.status === 'completed'));
  const meds = (visit.prescriptions || []).filter((p) => ['pending', 'dispensed'].includes(p.status));
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-[180px_1fr] gap-2 text-sm py-1 border-b border-gray-100"><span className="text-gray-600">{label}</span><span className="text-gray-900 whitespace-pre-line [overflow-wrap:anywhere]">{children || '—'}</span></div>
  );

  return (
    <>
      <style>{`@media print { .no-break { break-inside: avoid; } @page { margin: 1.2cm; } [data-testid='referral-letter-instruction'] { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <div className="min-h-screen bg-gray-50 p-8 print:p-0 print:bg-white">
        <div className="max-w-3xl mx-auto mb-4 print:hidden flex justify-between items-center bg-white shadow rounded-lg p-4">
          <div className="text-sm text-gray-600"><strong>Referral Letter</strong> — give to the patient. The patient must report to the Acacia Office first, then go to {ref.facility}.</div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} className="bg-violet-600 hover:bg-violet-700" data-testid="print-referral-button"><Printer className="w-4 h-4 mr-2" /> Print</Button>
            <Button variant="outline" onClick={() => window.close()}>Close</Button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg print:shadow-none p-10 print:p-6" data-testid="referral-letter">
          <div className="flex items-start justify-between border-b-2 border-gray-700 pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Send className="w-6 h-6" /> Referral Letter</h1>
              <p className="text-lg text-gray-700 font-medium">{visit.hospital_name}</p>
              <p className="text-xs text-gray-500">Acacia Medical Insurance — approved external referral</p>
            </div>
            <div className="text-right text-sm text-gray-700">
              <p><span className="text-gray-500">Referral #</span> <span className="font-bold font-mono" data-testid="referral-letter-number">{ref.number}</span></p>
              <p><span className="text-gray-500">Visit</span> {visit.visit_id}</p>
              <p><span className="text-gray-500">Date</span> {fmtDate(ref.referred_at)}</p>
              <p><span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold uppercase ${URGENCY_CLS[ref.urgency] || URGENCY_CLS.Routine}`} data-testid="referral-letter-urgency">{ref.urgency}</span></p>
            </div>
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Referred to</h2>
            <Row label="Facility"><strong data-testid="referral-letter-facility">{ref.facility}</strong></Row>
            <Row label="Specialist"><strong data-testid="referral-letter-specialist">{ref.specialist}</strong></Row>
            <Row label="Approved by Acacia office">{formatCATDateTime(ref.approved_at)}</Row>
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Patient</h2>
            <Row label="Name"><strong data-testid="referral-letter-patient">{visit.patient_name}</strong></Row>
            <Row label="Serial / Family">{visit.patient_serial_number} • {visit.family_id}</Row>
            <Row label="Sex / Date of birth">{visit.patient_sex || '—'} • {visit.patient_dob || '—'}</Row>
            {(visit.client_location || visit.client_phone) && <Row label="Location / Phone">{[visit.client_location, visit.client_phone].filter(Boolean).join(' • ')}</Row>}
          </div>

          <div className="mb-6 no-break">
            <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Clinical Summary</h2>
            {vitals && <Row label="Vital signs">{vitals}</Row>}
            <Row label="Presenting complaints">{c.complaints}</Row>
            {c.notes && <Row label="Physical examination">{c.notes}</Row>}
            <Row label="Provisional diagnosis">{c.provisional_diagnosis}</Row>
            {c.final_diagnosis && <Row label="Final diagnosis"><strong>{c.final_diagnosis}</strong></Row>}
          </div>

          {tests.length > 0 && (
            <div className="mb-6 no-break">
              <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Investigations & Results</h2>
              <table className="w-full text-sm table-fixed" data-testid="referral-letter-tests">
                <tbody>
                  {tests.map((t) => (
                    <tr key={t.order_id} className="border-b border-gray-100"><td className="py-1 pr-2 w-1/3 text-gray-800">{t.item_name}</td><td className="py-1 text-gray-700 break-words [overflow-wrap:anywhere]">{t.result || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {meds.length > 0 && (
            <div className="mb-6 no-break">
              <h2 className="text-sm font-bold uppercase text-gray-600 mb-1">Medicines given / prescribed</h2>
              <table className="w-full text-sm table-fixed" data-testid="referral-letter-medicines">
                <thead><tr className="text-left text-gray-600 border-b border-gray-300"><th className="py-1 w-2/5">Medicine</th><th className="py-1 w-16">Qty</th><th className="py-1">Instructions</th></tr></thead>
                <tbody>
                  {meds.map((p) => (
                    <tr key={p.rx_id} className="border-b border-gray-100"><td className="py-1 text-gray-900 break-words">{p.item_name}</td><td className="py-1">{p.quantity}</td><td className="py-1 text-gray-700 break-words [overflow-wrap:anywhere]">{p.instructions || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mb-8 no-break border-2 border-gray-800 rounded-lg p-4">
            <h2 className="text-sm font-bold uppercase text-gray-700 mb-1">Reason for referral</h2>
            <p className="text-base text-gray-900 whitespace-pre-line" data-testid="referral-letter-reason">{ref.reason}</p>
          </div>

          <div className="mb-8 no-break bg-amber-50 border-2 border-amber-500 rounded-lg p-4 print:bg-amber-50" data-testid="referral-letter-instruction">
            <h2 className="text-sm font-bold uppercase text-amber-900 mb-1">Important instruction to the patient</h2>
            <p className="text-base font-semibold text-amber-950">
              Before going to {ref.facility}, the patient MUST first report to the <strong>Acacia Office</strong> with this letter.
            </p>
            <p className="text-sm text-amber-900 mt-1">
              The Acacia Office will confirm the referral and arrange the visit to the referred hospital. The referred hospital should not attend to this referral until the Acacia Office has done so.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 text-sm text-gray-700 pt-4 no-break items-end" data-testid="referral-letter-signature">
            <div>
              <p className="text-xs uppercase text-gray-500 mb-1">Referring doctor</p>
              <p className="text-base font-bold text-gray-900" data-testid="referral-letter-doctor">{ref.doctor_name || ref.referred_by}</p>
              <p className="text-xs text-gray-600">{deptLabel(ref.from_department)} • {visit.hospital_name}</p>
              <div className="h-16 border-b border-gray-700 mt-2" />
              <p className="text-xs text-gray-500 mt-1">Signature & stamp</p>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-500 mb-1">Acacia Insurance</p>
              <p className="text-base text-gray-900">Approved {fmtDate(ref.approved_at)}</p>
              <p className="text-xs text-gray-600">Ref {ref.number} • Acacia Office</p>
              <div className="h-16 border-b border-gray-700 mt-2" />
              <p className="text-xs text-gray-500 mt-1">Acacia Insurance signature & stamp</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
